import { useEffect, useState } from 'react'
import {
  Plus,
  Trash2,
  Edit,
  ShieldAlert,
  Eye,
  Settings2,
  Send,
  HelpCircle,
  Hourglass,
  CheckCircle2,
  Ban,
  Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'
import teamGranularService from '@/services/teamGranularService'
import PermissionsMatrix from '@/components/PermissionsMatrix'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useConfirmacion } from '@/hooks/useConfirmacion'
import { getInitials } from '@/lib/utils'

const ROLE_INFO = {
  admin: { label: 'Admin', variant: 'danger', Icon: ShieldAlert },
  viewer: { label: 'Solo lectura', variant: 'secondary', Icon: Eye },
  custom: { label: 'Personalizado', variant: 'default', Icon: Settings2 },
}

const STATUS_INFO = {
  pending: { label: 'Invitación pendiente', variant: 'warning', Icon: Hourglass },
  active: { label: 'Activo', variant: 'success', Icon: CheckCircle2 },
  revoked: { label: 'Revocado', variant: 'secondary', Icon: Ban },
}

const RESEND_THROTTLE_MS = 5 * 60 * 1000

function relativeTime(iso) {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  if (diff < 60_000) return 'hace unos segundos'
  if (diff < 3_600_000) return `hace ${Math.round(diff / 60_000)} min`
  if (diff < 86_400_000) return `hace ${Math.round(diff / 3_600_000)} h`
  return `hace ${Math.round(diff / 86_400_000)} d`
}

function emptyForm() {
  return {
    _id: null,
    email: '',
    jobTitle: '',
    role: 'custom',
    preset: '',
    permissions: {},
  }
}

export default function TeamGranular() {
  const { confirmar, dialogo } = useConfirmacion()
  const { partner, user } = useAuthStore()
  const [members, setMembers] = useState([])
  const [presets, setPresets] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [form, setForm] = useState(emptyForm())
  const [resendingId, setResendingId] = useState(null)

  useEffect(() => {
    load()
  }, [])

  async function load() {
    try {
      setIsLoading(true)
      const [m, p] = await Promise.all([
        teamGranularService.list(),
        teamGranularService.getPresets(),
      ])
      setMembers(m.data || [])
      setPresets(p.data || [])
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al cargar equipo')
    } finally {
      setIsLoading(false)
    }
  }

  function openInvite() {
    setForm(emptyForm())
    setEditorOpen(true)
  }

  function openEdit(m) {
    setForm({
      _id: m._id,
      email: m.email || '',
      jobTitle: m.jobTitle || '',
      role: m.role || 'custom',
      preset: '',
      permissions: { ...(m.permissions || {}) },
    })
    setEditorOpen(true)
  }

  function applyPreset(presetKey) {
    const preset = presets.find((p) => p.key === presetKey)
    if (!preset) return
    setForm((f) => ({
      ...f,
      preset: presetKey,
      permissions: { ...preset.permissions },
      role: 'custom',
    }))
  }

  async function save() {
    if (!form._id && !form.email?.trim()) {
      toast.error('Email obligatorio')
      return
    }
    try {
      setIsSaving(true)
      if (form._id) {
        await teamGranularService.update(form._id, {
          jobTitle: form.jobTitle,
          role: form.role,
          preset: form.preset || undefined,
          permissions: form.permissions,
        })
        toast.success('Permisos actualizados')
      } else {
        const inviteResp = await teamGranularService.invite({
          email: form.email,
          jobTitle: form.jobTitle,
          role: form.role,
          preset: form.preset || undefined,
          permissions: form.permissions,
        })
        const created = inviteResp?.data || inviteResp
        toast.success(
          created?.userExists
            ? `Invitación enviada a ${form.email}. Confirmará el acceso al abrir el enlace.`
            : `Invitación enviada a ${form.email}. Recibirá un correo para crear su cuenta (expira en 7 días).`,
          { duration: 6000 }
        )
      }
      setEditorOpen(false)
      await load()
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al guardar')
    } finally {
      setIsSaving(false)
    }
  }

  async function remove(member) {
    const ok = await confirmar({
      titulo: 'Quitar del equipo',
      descripcion: `¿Quitas a ${member.email || member.name} del equipo? Pierde el acceso al Country Hub. Su historial se queda.`,
      textoConfirmar: 'Quitar',
      variante: 'destructiva',
    })
    if (!ok) return
    try {
      await teamGranularService.remove(member._id)
      toast.success('Miembro removido')
      await load()
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error')
    }
  }

  async function resendInvite(member) {
    try {
      setResendingId(member._id)
      await teamGranularService.resend(member._id)
      toast.success(`Invitación reenviada a ${member.email}`)
      await load()
    } catch (e) {
      toast.error(e.response?.data?.message || 'No se pudo reenviar')
    } finally {
      setResendingId(null)
    }
  }

  const canResend = (m) => {
    if (m.status !== 'pending') return false
    if (!m.lastInviteSentAt) return true
    return Date.now() - new Date(m.lastInviteSentAt).getTime() >= RESEND_THROTTLE_MS
  }

  const isAdmin = user?.partnerRole === 'admin'

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex justify-center py-12">
          <Loader2 size={28} className="animate-spin text-primary" />
        </div>
      </PageContainer>
    )
  }

  if (partner?.organizationType !== 'country_manager') {
    return (
      <PageContainer>
        <Alert variant="info">
          <AlertDescription>
            La gestión granular de equipo es exclusiva del Country Manager.
          </AlertDescription>
        </Alert>
      </PageContainer>
    )
  }

  return (
    <TooltipProvider delayDuration={200}>
      <PageContainer>
        <PageHeader
          title="Equipo del Country Hub"
          description="Invita miembros y delega permisos específicos. Tú (admin) siempre tienes acceso completo."
          actions={
            <>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setHelpOpen(true)}
                aria-label="Ayuda"
              >
                <HelpCircle size={16} />
              </Button>
              {isAdmin && (
                <Button onClick={openInvite}>
                  <Plus size={16} /> Invitar miembro
                </Button>
              )}
            </>
          }
        />

        {!isAdmin && (
          <Alert variant="info">
            <AlertDescription>
              Solo el admin del partner puede invitar y modificar permisos.
            </AlertDescription>
          </Alert>
        )}

        {members.length === 0 ? (
          <Card>
            <CardContent className="text-center py-10">
              <h3 className="font-display text-h2 font-semibold text-foreground mb-1">
                Aún no tienes equipo
              </h3>
              <p className="text-body-sm text-subtle mb-4">
                Invita colaboradores y delega tareas específicas.
              </p>
              {isAdmin && (
                <Button onClick={openInvite}>
                  <Plus size={16} /> Invitar primero
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {members.map((m) => {
              const role = ROLE_INFO[m.role] || ROLE_INFO.custom
              const status = STATUS_INFO[m.status || 'pending']
              const grantedCount = m.permissions
                ? Object.values(m.permissions).filter(Boolean).length
                : 0
              const detail =
                m.status === 'pending' && m.lastInviteSentAt
                  ? `enviada ${relativeTime(m.lastInviteSentAt)}`
                  : m.status === 'active' && m.lastActiveAt
                    ? `última actividad ${relativeTime(m.lastActiveAt)}`
                    : m.status === 'active' && m.acceptedAt
                      ? `aceptada ${relativeTime(m.acceptedAt)}`
                      : ''
              const RoleIcon = role.Icon
              const StatusIcon = status.Icon
              return (
                <Card key={m._id}>
                  <CardContent className="pt-5 pb-5">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center">
                      <Avatar>
                        <AvatarFallback>
                          {getInitials(m.name || m.email)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 mb-1">
                          <p className="font-semibold text-foreground truncate">
                            {m.name || m.email}
                          </p>
                          <Badge variant={role.variant} className="gap-1">
                            <RoleIcon size={11} /> {role.label}
                          </Badge>
                          <Badge variant={status.variant} className="gap-1">
                            <StatusIcon size={11} /> {status.label}
                          </Badge>
                          {m.role === 'custom' && (
                            <Badge variant="outline">{grantedCount} permisos</Badge>
                          )}
                        </div>
                        <p className="text-caption text-subtle truncate">
                          {m.jobTitle ? `${m.jobTitle} · ` : ''}
                          {m.email}
                          {detail ? ` · ${detail}` : ''}
                        </p>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-1">
                          {m.status === 'pending' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => resendInvite(m)}
                                    disabled={!canResend(m) || resendingId === m._id}
                                    aria-label="Reenviar invitación"
                                  >
                                    {resendingId === m._id ? (
                                      <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                      <Send size={14} />
                                    )}
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {canResend(m)
                                  ? 'Reenviar invitación'
                                  : 'Espera 5 minutos entre reenvíos'}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(m)}
                            aria-label="Editar"
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => remove(m)}
                            aria-label="Remover"
                            className="text-danger hover:bg-danger-50 dark:hover:bg-danger-500/15"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}

        <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                {form._id ? 'Editar miembro' : 'Invitar miembro'}
              </DialogTitle>
            </DialogHeader>

            {!form._id && (
              <Alert variant="info">
                <AlertDescription>
                  Recibirá un correo con un enlace único para entrar al Country Hub. Si
                  aún no tiene cuenta, podrá crearla desde ahí. El enlace expira en 7
                  días.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="tg-email">Email</Label>
                  <Input
                    id="tg-email"
                    type="email"
                    value={form.email}
                    disabled={!!form._id}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tg-job">Cargo / título</Label>
                  <Input
                    id="tg-job"
                    value={form.jobTitle}
                    onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
                    placeholder="ej: Coordinadora de eventos"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Rol base</Label>
                  <Select
                    value={form.role}
                    onValueChange={(v) => setForm({ ...form, role: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin (acceso completo)</SelectItem>
                      <SelectItem value="custom">
                        Custom (permisos granulares)
                      </SelectItem>
                      <SelectItem value="viewer">Solo lectura</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Aplicar preset</Label>
                  <Select
                    value={form.preset || 'none'}
                    onValueChange={(v) => v !== 'none' && applyPreset(v)}
                    disabled={form.role !== 'custom'}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Sin preset —</SelectItem>
                      {presets.map((p) => (
                        <SelectItem key={p.key} value={p.key}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {form.preset && form.role === 'custom' && (
                <Alert variant="info">
                  <AlertDescription>
                    Preset aplicado:{' '}
                    <strong>
                      {presets.find((p) => p.key === form.preset)?.label}
                    </strong>
                    . Puedes ajustar permisos individuales abajo.
                  </AlertDescription>
                </Alert>
              )}

              <PermissionsMatrix
                value={form.permissions}
                onChange={(next) =>
                  setForm((f) => ({ ...f, permissions: next }))
                }
                disabled={form.role !== 'custom'}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditorOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={save} disabled={isSaving}>
                {isSaving ? 'Guardando…' : form._id ? 'Guardar' : 'Invitar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>¿Cómo funciona la invitación?</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {[
                {
                  title: '1. Tú invitas',
                  body: 'Asignas un rol o preset, y opcionalmente ajustas permisos. Al guardar, enviamos un correo con un enlace único válido por 7 días.',
                },
                {
                  title: '2. La persona acepta',
                  body: 'Hace clic en el enlace. Si no tiene cuenta, la crea ahí. Si ya tiene, inicia sesión y confirma.',
                },
                {
                  title: '3. Acceso inmediato',
                  body: 'Tras aceptar entra al Country Hub con los permisos asignados. Su estado pasa de Invitación pendiente a Activo.',
                },
                {
                  title: 'Reenvío y revocación',
                  body: 'Si el enlace expira, usa reenviar. Para retirar acceso, elimina al miembro — esto no borra historial.',
                },
              ].map((s) => (
                <div key={s.title}>
                  <p className="font-semibold text-foreground text-body-sm">
                    {s.title}
                  </p>
                  <p className="text-body-sm text-subtle mt-0.5">{s.body}</p>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={() => setHelpOpen(false)}>Entendido</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {dialogo}
      </PageContainer>
    </TooltipProvider>
  )
}
